import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Box, useMediaQuery } from "@mui/material";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import moment from "moment";
import api from "../../../core/api";
import useTranslation from "../../../shared/hooks/useTranslation";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import Loader from "../../../shared/components/Loader";
import { CustomerMachineState, Machine } from "../../machinery/models/models";
import { ServiceReportFile, ServiceReport as ServiceReportModel, ServiceReportsState } from "../../machinery/models/ServiceReportsModel";
import { getMachineParts, getServiceReport as fetchServiceReport } from "../../machinery/customerMachineServiceReportsSlice";
import { getMachine, clearMachine } from "../../machinery/customerMachinerySlice";
import { getCustomer, PublicState } from "../../../public/publicSlice";
import {
  ChangeServiceRequest,
  CreateServiceRequest,
  ServiceRequest,
  ServiceRequestFormData,
  ServiceRequestsState,
} from "../models/models";
import {
  changeServiceRequest,
  clearServiceRequest,
  createServiceRequest,
  getServiceRequest,
  getServiceRequests,
  publishServiceRequest,
} from "../customerServicingSlice";
import { useServiceRequestConverters } from "../hooks/useServiceRequestConverters";
import usePermission from "../../../shared/hooks/usePermission";
import { Modules } from "../../../core/models/PermissionModels";

import { dt } from "../../../shared/themes/redesignTokens";
import { useRedesignSetup } from "../../../shared/hooks/useRedesignSetup";
import WizardStepper from "../../serviceOffers/components/WizardStepper";
import StepMachineAndDetails from "./StepMachineAndDetails";
import StepSpecification from "./StepSpecification";
import ServiceRequestPreview from "./ServiceRequestPreview";
import VisibilitySelector, { PartnerCompany } from "./VisibilitySelector";
import SelectPartnersDialog from "./SelectPartnersDialog";
import RfsWizardNavigation from "./RfsWizardNavigation";
import locationsApi from "../api/locationsApi";
import type { Location } from "./locations/locationTypes";

// ═══════════════════════════════════════════════════════════════
//  CONSTANTS
// ═══════════════════════════════════════════════════════════════

const STEP_COUNT = 3;

// ═══════════════════════════════════════════════════════════════
//  COMPONENT
// ═══════════════════════════════════════════════════════════════

const EditServiceRequestPage = () => {
  useRedesignSetup();

  const { translate } = useTranslation(["myMachinery", "servicing", "common"]);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { id } = useParams();
  const { toServiceRequestBase, toPreviewServiceRequest } = useServiceRequestConverters();
  const { canWrite } = usePermission(Modules.Servicing);
  const isCreating = !id;
  const isReadOnly = isCreating ? false : !canWrite;
  const isMobile = useMediaQuery("(max-width:768px)");

  const dispatch = useAppDispatch();
  const { serviceRequest, serviceRequestsQuery, loading: serviceRequestLoading } =
    useAppSelector<ServiceRequestsState>((state: any) => state.customerServicing);
  const { machine } = useAppSelector<CustomerMachineState>((state: any) => state.customerMachinery);
  const { customer } = useAppSelector<PublicState>((state: any) => state.public);
  const { serviceReports, machineParts } = useAppSelector<ServiceReportsState>(
    (state: any) => state.customerServiceReports
  );

  const initialMachineId = searchParams.get("machine");
  const initialServiceReportNumber = searchParams.get("report");
  const returnTo = searchParams.get("returnTo");

  // ── Form state ─────────────────────────────────────────
  const [selectedMachine, setSelectedMachine] = useState<Machine | undefined>(undefined);
  const [selectedReportNumber, setSelectedReportNumber] = useState<string | undefined>(undefined);
  const [files, setFiles] = useState<ServiceReportFile[]>([]);
  const [previewServiceRequest, setPreviewServiceRequest] = useState<ServiceRequest>();

  // ── Location state ────────────────────────────────────
  const [locations, setLocations] = useState<Location[]>([]);
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);

  // ── Visibility state ───────────────────────────────────
  const [visibilityCode, setVisibilityCode] = useState<number>(1);
  const [targetPartnerIds, setTargetPartnerIds] = useState<string[]>([]);
  const [partners, setPartners] = useState<PartnerCompany[]>([]);
  const [partnersLoading, setPartnersLoading] = useState(false);
  const [visibilityError, setVisibilityError] = useState<string | undefined>();
  const [selectPartnersDialogOpen, setSelectPartnersDialogOpen] = useState(false);

  // ── Wizard state ───────────────────────────────────────
  const [activeStepIndex, setActiveStepIndex] = useState(0);

  const serviceRequestNumber = serviceRequest?.internalNumber || undefined;
  const serviceRequestId = serviceRequest?.id || undefined;
  const isNew: boolean = !serviceRequestNumber;

  const stepLabels = useMemo(
    () => [
      translate("servicing:stepMachineDetails"),
      translate("servicing:stepSpecification"),
      translate("servicing:stepPreviewPublish"),
    ],
    [translate]
  );

  // ── Form setup ─────────────────────────────────────────
  const formMethods = useForm<ServiceRequestFormData>();
  const { handleSubmit, reset, trigger, getValues, setValue } = formMethods;

  const emptyFormData = useMemo<ServiceRequestFormData>(
    () => ({
      issue: "",
      description: "",
      openDate: new Date(),
      affectedPartCategoryId: "",
      flexibleDates: false,
      worksiteAccessEnabled: false,
      budgetEnabled: false,
      paymentTermsEnabled: false,
      notes: "",
      alarmCode: "",
    }),
    []
  );

  // ── Fetch partners on mount ────────────────────────────
  useEffect(() => {
    if (customer?.id) {
      setPartnersLoading(true);
      const customersBaseUrl = (window as any).app?.config?.customersApiBaseUrl;
      if (customersBaseUrl) {
        api
          .get<PartnerCompany[]>(`/v1/public/partnerships/partners/${customer.id}`, {
            baseURL: customersBaseUrl,
          })
          .then((res) => {
            const mapped = (res.data || []).map((p: any) => ({
              customerId: p.id || p.customerId,
              name: p.name,
              logoUrl: p.logoUrl,
              tierCode: p.effectiveTier ?? p.tierCode ?? 1,
            }));
            setPartners(mapped);
          })
          .catch(() => setPartners([]))
          .finally(() => setPartnersLoading(false));
      } else {
        setPartnersLoading(false);
      }
    }
  }, [customer?.id]);

  // ── Fetch locations for the company ───────────────────
  useEffect(() => {
    if (customer?.id) {
      locationsApi
        .getLocationsByCustomer(customer.id)
        .then((locs) => setLocations(locs || []))
        .catch(() => setLocations([]));
    }
  }, [customer?.id]);

  // ── Auto-fill location from selected machine ─────────
  useEffect(() => {
    if (selectedMachine?.locationId && !selectedLocationId) {
      setSelectedLocationId(selectedMachine.locationId);
      setValue("locationId", selectedMachine.locationId);
    }
  }, [selectedMachine?.locationId]);

  // ── Initialize visibility from existing SR ─────────────
  useEffect(() => {
    if (serviceRequest) {
      setVisibilityCode(serviceRequest.visibilityCode || 1);
      // Backward compat: existing SRs have single targetPartnerId
      setTargetPartnerIds(
        serviceRequest.targetPartnerId ? [serviceRequest.targetPartnerId] : []
      );
      // Initialize location from existing SR
      if (serviceRequest.locationId) {
        setSelectedLocationId(serviceRequest.locationId);
      }
    }
  }, [serviceRequest]);

  // ── Submit handler ─────────────────────────────────────
  const onSubmit = async (data: ServiceRequestFormData, shouldPublish: boolean = false) => {
    // Validate partner selection when "Send to Partner" is chosen
    if (visibilityCode === 2 && targetPartnerIds.length === 0) {
      setVisibilityError(translate("servicing:visibility.validationRequired"));
      return;
    }
    setVisibilityError(undefined);

    const model = toServiceRequestBase(data, files);
    // Backward compat: send first selected partner as targetPartnerId
    const firstPartnerId = targetPartnerIds.length > 0 ? targetPartnerIds[0] : undefined;

    try {
      if (isNew) {
        if (!selectedMachine?.id) {
          return;
        }

        const createModel: CreateServiceRequest = {
          ...model,
          machineId: selectedMachine.id,
          serviceReportNumber: selectedReportNumber,
          visibilityCode,
          targetPartnerId: visibilityCode === 2 ? firstPartnerId : undefined,
          targetPartnerIds: visibilityCode === 2 ? targetPartnerIds : undefined,
          locationId: selectedLocationId || undefined,
        };

        const createResult = await dispatch(createServiceRequest(createModel)).unwrap();
        if (shouldPublish) {
          await dispatch(publishServiceRequest({
            id: createResult.id,
            visibilityCode,
            targetPartnerIds: visibilityCode === 2 ? targetPartnerIds : undefined,
          })).unwrap();
        }
      } else {
        if (!serviceRequestId) {
          return;
        }

        const changeModel: ChangeServiceRequest = {
          ...model,
          visibilityCode,
          targetPartnerId: visibilityCode === 2 ? firstPartnerId : undefined,
          targetPartnerIds: visibilityCode === 2 ? targetPartnerIds : undefined,
          locationId: selectedLocationId || undefined,
        };
        await dispatch(changeServiceRequest({ serviceRequestId, model: changeModel }));
        if (shouldPublish) {
          await dispatch(publishServiceRequest({
            id: serviceRequestId,
            visibilityCode,
            targetPartnerIds: visibilityCode === 2 ? targetPartnerIds : undefined,
          })).unwrap();
        }
      }

      dispatch(getServiceRequests(serviceRequestsQuery));
      dispatch(clearServiceRequest());
      dispatch(clearMachine());
      setSelectedMachine(undefined);
      const destination = returnTo || "/service-requests";
      navigate(shouldPublish ? `${destination}${destination.includes("?") ? "&" : "?"}published=true` : destination);
    } catch (error) {}
  };

  // ── Step navigation ────────────────────────────────────
  const handleNext = async () => {
    if (activeStepIndex === 0) {
      // Validate step 1: machine must be selected, required form fields
      if (!selectedMachine) {
        return;
      }
      const isValid = await trigger(["issue", "affectedPartCategoryId", "openDate"]);
      if (!isValid) return;
    }

    if (activeStepIndex === 1) {
      // Validate step 2: service specification
      const isValid = await trigger(["serviceTypeCode", "priorityTypeCode"]);
      if (!isValid) return;

      // Build preview before going to step 3
      const data = getValues();
      const preview = toPreviewServiceRequest(data, files);
      preview.id = serviceRequest?.id || "";
      setPreviewServiceRequest(preview);
    }

    setActiveStepIndex((prev) => Math.min(prev + 1, STEP_COUNT - 1));
  };

  const handleBack = () => {
    setActiveStepIndex((prev) => Math.max(prev - 1, 0));
  };

  const handleSaveAsDraft: SubmitHandler<ServiceRequestFormData> = async (data) => {
    await onSubmit(data, false);
  };

  const handlePublish = async () => {
    // Validate visibility on final step
    if (visibilityCode === 2 && targetPartnerIds.length === 0) {
      setVisibilityError(translate("servicing:visibility.validationRequired"));
      return;
    }
    setVisibilityError(undefined);

    const isValid = await trigger();
    if (isValid) {
      const formData = getValues();
      await onSubmit(formData, true);
    }
  };

  const handleCancel = () => {
    reset();
    dispatch(clearServiceRequest());
    dispatch(clearMachine());
    setSelectedMachine(undefined);
    navigate(returnTo || "/service-requests");
  };

  const handleStepClick = (step: number) => {
    if (step < activeStepIndex) {
      setActiveStepIndex(step);
    }
  };

  const handleSaveDraft = async () => {
    const data = getValues();
    await onSubmit(data, false);
  };

  // ── Initialize from service report URL params ──────────
  const initializeFromServiceReport = async (
    machineId: string,
    serviceReportNumber?: string | null
  ) => {
    const machine = await dispatch(getMachine(machineId)).unwrap();
    setSelectedMachine(machine);

    // Setting selectedReportNumber triggers the detail-fetch effect above,
    // which loads the report's data (including files) via the detail endpoint.
    if (serviceReportNumber) {
      setSelectedReportNumber(serviceReportNumber);
    }
  };

  useEffect(() => {
    if (selectedMachine || selectedReportNumber) {
      return;
    }

    if (!initialMachineId) {
      return;
    }

    initializeFromServiceReport(initialMachineId, initialServiceReportNumber);
  }, [initialMachineId, initialServiceReportNumber, dispatch]);

  // ── Clear stale Redux state when creating a new SR ────
  // CRITICAL: When navigating from ViewServiceRequestPage to /service-requests/new,
  // the Redux state may still hold the previously viewed SR. If we don't clear it,
  // the form pre-fills with old data AND `isNew` evaluates to false, causing the
  // submit handler to call changeServiceRequest (PUT) instead of createServiceRequest (POST).
  // This overwrites the old SR instead of creating a new one — causing data corruption.
  useEffect(() => {
    if (!id) {
      dispatch(clearServiceRequest());
      dispatch(clearMachine());
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (id) {
      dispatch(getServiceRequest(id));
    }
    dispatch(getMachineParts());
  }, [dispatch, id]);

  useEffect(() => {
    if (!serviceRequest) {
      // Only reset form + files if we're NOT loading from a service report.
      // When selectedReportNumber is set, the report-loading effect handles
      // both form fields and files — resetting here would clear pre-filled data.
      if (!selectedReportNumber) {
        reset(emptyFormData);
        setFiles([]);
      }
      return;
    }

    if (serviceRequest.machine) {
      dispatch(getMachine(serviceRequest.machine.id));
    }

    const serviceRequestFormData: ServiceRequestFormData = {
      ...serviceRequest,
      workingHoursFrom: serviceRequest.workingHoursFrom
        ? moment(serviceRequest.workingHoursFrom, ["HH:mm:ss"])
        : undefined,
      workingHoursTo: serviceRequest.workingHoursTo
        ? moment(serviceRequest.workingHoursTo, ["HH:mm:ss"])
        : undefined,
    };

    reset(serviceRequestFormData);
    setFiles(serviceRequest.files || []);
  }, [serviceRequest, reset, emptyFormData, dispatch, selectedMachine]);

  useEffect(() => {
    if (machine && !selectedMachine) {
      setSelectedMachine(machine);
    }
  }, [machine, selectedMachine]);

  useEffect(() => {
    if (selectedMachine) {
      dispatch(getMachine(selectedMachine.id));

      if (selectedMachine.customerId !== customer?.id) {
        dispatch(getCustomer(selectedMachine.customerId));
      }
    }
  }, [selectedMachine]);

  useEffect(() => {
    return () => {
      dispatch(clearServiceRequest());
      dispatch(clearMachine());
      setSelectedMachine(undefined);
    };
  }, []);

  // ── Fetch report detail + pre-fill form when a report is selected ──
  // Uses the DETAIL endpoint (not the list) so files are always included,
  // regardless of whether the list was cleared by the AttachReportDialog cleanup.
  const loadedReportNumberRef = React.useRef<string | undefined>(undefined);
  const loadedReportRef = React.useRef<ServiceReportModel | undefined>(undefined);

  useEffect(() => {
    if (!selectedReportNumber || selectedReportNumber === loadedReportNumberRef.current) {
      return;
    }

    let cancelled = false;

    const applyReport = (report: ServiceReportModel) => {
      loadedReportNumberRef.current = selectedReportNumber;
      loadedReportRef.current = report;

      // Ensure machineParts are loaded before applying form values.
      // If not loaded yet, dispatch and let the machineParts useEffect below re-apply.
      if (!machineParts?.length) {
        dispatch(getMachineParts());
      }

      const currentValues = getValues();
      reset({
        ...currentValues,
        issue: report.title,
        affectedPartCategoryId: report.affectedPartCategoryId,
        description: report.description || currentValues.description,
        notes: report.notes || currentValues.notes,
        alarmCode: report.alarmCode || currentValues.alarmCode,
        openDate: report.openDate
          ? new Date(report.openDate)
          : currentValues.openDate,
        priorityTypeCode: report.urgency || currentValues.priorityTypeCode,
        serviceTypeCode: report.serviceTypeCode || currentValues.serviceTypeCode,
      });

      const reportFiles = (report.files || []).map((file) => ({ ...file }));
      setFiles(reportFiles);
    };

    const loadReport = async () => {
      try {
        const report: ServiceReportModel = await dispatch(fetchServiceReport(selectedReportNumber)).unwrap();
        if (cancelled) return;
        applyReport(report);
      } catch (err) {
        // If the detail fetch fails, fall back to finding in the list
        if (cancelled) return;
        if (serviceReports) {
          const fallback = serviceReports.find((r) => r.number === selectedReportNumber);
          if (fallback) {
            applyReport(fallback);
          }
        }
      }
    };

    loadReport();

    return () => {
      cancelled = true;
    };
  }, [selectedReportNumber]);

  // ── SRE-RFS-2: Re-apply affectedPartCategoryId once machineParts load ──
  // When loadReport fires before getMachineParts() completes, partOptions in
  // StepMachineAndDetails has no categoryId values, so the Autocomplete shows
  // blank. Once machineParts arrive, we re-set the form value to trigger a
  // re-render of the Autocomplete with the now-resolvable categoryId.
  useEffect(() => {
    if (!machineParts?.length || !loadedReportRef.current) return;

    const report = loadedReportRef.current;
    if (!report.affectedPartCategoryId) return;

    const currentValues = getValues();
    // Only re-apply if the form value matches the report (user hasn't changed it)
    if (currentValues.affectedPartCategoryId === report.affectedPartCategoryId) {
      // Use setValue with shouldValidate to force the Autocomplete to re-evaluate
      // now that partOptions have resolved categoryIds from the loaded machineParts
      setValue("affectedPartCategoryId", report.affectedPartCategoryId, { shouldValidate: true });
    }
  }, [machineParts, getValues, setValue]);

  // ── Loading gate ───────────────────────────────────────
  const isServiceRequestPreparing = serviceRequestLoading;
  const isMachinePreparing = serviceRequest && !selectedMachine;
  if (isServiceRequestPreparing || isMachinePreparing) {
    return <Loader />;
  }

  // ═══════════════════════════════════════════════════════
  //  RENDER
  // ═══════════════════════════════════════════════════════

  return (
    <FormProvider {...formMethods}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: "100%",
          fontFamily: dt.font,
          "& *:not(.MuiSvgIcon-root)": { fontFamily: `${dt.font}` },
          background: dt.pageBg,
          backgroundImage: `radial-gradient(circle, ${dt.pageDotColor} 1px, transparent 1px)`,
          backgroundSize: "20px 20px",
        }}
      >
        {/* ═══ Header bar ═══ */}
        <Box
          sx={{
            background: "#FFFFFF",
            borderBottom: `1px solid ${dt.cardBorder}`,
            px: isMobile ? 2 : 4,
            py: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
            animation: "headerReveal 0.5s cubic-bezier(0.4,0,0.2,1) both",
          }}
        >
          <Box>
            <Box
              sx={{
                fontFamily: dt.font,
                fontSize: { xs: "16px", md: "18px" },
                fontWeight: 700,
                color: dt.textPrimary,
              }}
            >
              {isNew
                ? translate("myMachinery:createServiceRequest")
                : `${translate("myMachinery:editServiceRequest")} ${serviceRequest?.internalNumber}`}
            </Box>
          </Box>

          <WizardStepper
            steps={stepLabels}
            activeStep={activeStepIndex}
            onStepClick={handleStepClick}
          />
        </Box>

        {/* ═══ Content area ═══ */}
        <Box
          component="form"
          id="changeServiceRequest"
          onSubmit={handleSubmit(handleSaveAsDraft)}
          sx={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
            px: isMobile ? 2 : 4,
            py: 3,
            pb: 12,
            overflowX: "hidden",
          }}
        >
          <Box sx={{ width: "100%", maxWidth: "1200px" }}>
            {/* Step 1: Machine & Details */}
            {activeStepIndex === 0 && (
              <StepMachineAndDetails
                selectedMachine={selectedMachine}
                files={files}
                onSelectedMachineChange={setSelectedMachine}
                onSelectedReportNumberChange={setSelectedReportNumber}
                onFilesChange={setFiles}
                locations={locations}
                selectedLocationId={selectedLocationId}
                onLocationChange={setSelectedLocationId}
              />
            )}

            {/* Step 2: Specification */}
            {activeStepIndex === 1 && <StepSpecification />}

            {/* Step 3: Visibility + Preview */}
            {activeStepIndex === 2 && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 3,
                  animation: "fadeUp 0.5s cubic-bezier(0.4,0,0.2,1) both",
                }}
              >
                {/* Visibility selector (elevated card-based) */}
                <Box
                  sx={{
                    backgroundColor: dt.cardBg,
                    borderRadius: dt.radius,
                    border: `1px solid ${dt.cardBorder}`,
                    boxShadow: dt.cardShadow,
                    p: { xs: 2.5, md: 4 },
                  }}
                >
                  <VisibilitySelector
                    visibilityCode={visibilityCode}
                    setVisibilityCode={setVisibilityCode}
                    targetPartnerIds={targetPartnerIds}
                    setTargetPartnerIds={setTargetPartnerIds}
                    partners={partners}
                    partnersLoading={partnersLoading}
                    visibilityError={visibilityError}
                    setVisibilityError={setVisibilityError}
                    translate={translate}
                    onOpenSelectPartners={() => setSelectPartnersDialogOpen(true)}
                  />
                </Box>

                {/* Preview */}
                <Box
                  sx={{
                    backgroundColor: dt.cardBg,
                    borderRadius: dt.radius,
                    border: `1px solid ${dt.cardBorder}`,
                    boxShadow: dt.cardShadow,
                    p: { xs: 2, md: 3 },
                  }}
                >
                  <Box
                    sx={{
                      fontFamily: dt.font,
                      fontSize: { xs: "16px", md: "18px" },
                      fontWeight: 700,
                      color: dt.textPrimary,
                      mb: 2,
                    }}
                  >
                    {translate("servicing:previewPublish")}
                  </Box>
                  <ServiceRequestPreview serviceRequest={previewServiceRequest} />
                </Box>
              </Box>
            )}
          </Box>
        </Box>

        {/* ═══ Bottom navigation bar ═══ */}
        <RfsWizardNavigation
          activeStep={activeStepIndex}
          totalSteps={STEP_COUNT}
          isSubmitting={isReadOnly}
          onNext={handleNext}
          onBack={handleBack}
          onSaveDraft={isReadOnly ? () => {} : handleSaveDraft}
          onSubmit={isReadOnly ? () => {} : handlePublish}
          onCancel={handleCancel}
        />
      </Box>

      {/* ═══ Select Partners Dialog ═══ */}
      <SelectPartnersDialog
        open={selectPartnersDialogOpen}
        onClose={() => setSelectPartnersDialogOpen(false)}
        partners={partners}
        selectedIds={targetPartnerIds}
        onConfirm={(ids) => {
          setTargetPartnerIds(ids);
          setVisibilityError(undefined);
        }}
        translate={translate}
      />
    </FormProvider>
  );
};

export default EditServiceRequestPage;
